import React, { useState } from 'react';
import { BoldIcon, ItalicIcon, UnderlineIcon, StrikethroughIcon, LinkIcon, CodeIcon, QuoteIcon, ListIcon, ListOrderedIcon, AlignLeftIcon, GripVerticalIcon, PencilIcon, TrashIcon, PlusIcon, SmartphoneIcon, CreditCardIcon } from 'lucide-react';
type Section = 'cartes' | 'famille' | 'residences' | 'vehicules' | 'animaux';
type Language = 'fr' | 'en';
interface Justificatif {
  id: string;
  type: string;
  description: string;
}
interface RefusalReason {
  id: string;
  titre: string;
  descriptionInterne: string;
  motifCitoyen: string;
}
interface InstructionField {
  label: string;
  content: string;
}
interface CardElement {
  id: string;
  label: string;
  enabled: boolean;
}
interface SectionState {
  justificatifs: Justificatif[];
  instructions: InstructionField[];
  guide: string;
  refusalLang: Language;
  refusals: RefusalReason[];
}
const SECTION_LABELS: Record<Section, string> = {
  cartes: 'Cartes',
  famille: 'Famille',
  residences: 'Propriétés',
  vehicules: 'Véhicules',
  animaux: 'Animaux'
};
const SECTION_ORDER: Section[] = ['cartes', 'famille', 'residences', 'vehicules', 'animaux'];
const INITIAL_STATE: Record<Section, SectionState> = {
  cartes: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Permis de conduire valide'
    }, {
      id: '2',
      type: 'carte_id',
      description: "Carte d'identité avec photo"
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez remplir vos renseignements personnels. Ces données seront validées par le personnel de la Ville.'
    }, {
      label: "Instructions d'adhésion à la carte",
      content: "Une carte citoyenne est requise afin de vous doter d'un abonnement."
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Carte d'identité avec photo, nom et adresse complète (ex: permis de conduire). Recto ET Verso\nou\n• Une carte d'identité avec photo SANS adresse complète (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral) ou un compte de taxes (municipales ou scolaires) de l'année courante ou précédente."
    }, {
      label: 'Confirmation',
      content: "Veuillez vérifier que vos informations sont exactes avant de confirmer l'envoi."
    }, {
      label: "Texte sous le bouton 'Adhérer'",
      content: "Vous n'avez jamais possédé de carte à la Ville de Windsor."
    }, {
      label: "Texte sous le bouton 'Lier'",
      content: 'Vous avez déjà possédé une carte à la Ville de Windsor.'
    }],
    guide: "Documents acceptés :\n• Carte d'identité avec photo, nom et adresse complète (ex: permis de conduire). Recto ET Verso\nou\n• Une carte d'identité avec photo SANS adresse complète (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral) ou un compte de taxes (municipales ou scolaires) de l'année courante ou précédente.",
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Carte citoyenne - Mauvaise preuve - Adulte',
      descriptionInterne: 'Carte citoyenne - Mauvaise preuve - Adulte',
      motifCitoyen: "La preuve de résidence n'est pas valide. Les preuves acceptées sont le permis de conduire (recto et verso). Si vous n'en possédez pas, vous devez nous faire parvenir deux documents: une carte d'identité avec photo (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral), ou un compte de taxes (municipales ou scolaires) de moins de trois mois dont votre adresse de Windsor et votre nom sont inscrits dessus. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-2077 ou par courriel à services@villedewindsor.qc.ca."
    }, {
      id: '2',
      titre: 'Carte citoyenne - Aucune preuve avec photo',
      descriptionInterne: 'Carte citoyenne - Aucune preuve avec photo',
      motifCitoyen: "Il manque une pièce d'identité avec photo. Les preuves acceptées sont le permis de conduire (recto et verso). Si vous n'en possédez pas, vous devez nous faire parvenir deux documents: une carte d'identité avec photo (ex : carte d'assurance maladie) ET un document officiel d'un palier gouvernemental (provincial ou fédéral), ou un compte de taxes (municipales ou scolaires) de moins de trois mois dont votre adresse de Windsor est inscrite dessus. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-2077 ou par courriel à services@villedewindsor.qc.ca."
    }]
  },
  famille: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Permis de conduire valide'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez remplir les informations de votre famille.'
    }, {
      label: 'Pièces justificatives',
      content: 'Documents requis pour la validation familiale.'
    }],
    guide: 'Guide pour valider les informations familiales.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Famille - Documents incomplets',
      descriptionInterne: 'Famille - Documents incomplets',
      motifCitoyen: 'Les documents fournis sont incomplets. Veuillez soumettre tous les justificatifs requis.'
    }]
  },
  residences: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: 'Preuve de résidence'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez fournir une preuve de résidence valide.'
    }, {
      label: 'Pièces justificatives',
      content: 'Documents acceptés pour prouver votre résidence.'
    }],
    guide: 'Guide pour valider la résidence.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Propriété - Preuve invalide',
      descriptionInterne: 'Propriété - Preuve invalide',
      motifCitoyen: "La preuve de propriété fournie n'est pas valide. Veuillez soumettre un document officiel récent."
    }]
  },
  vehicules: {
    justificatifs: [{
      id: '1',
      type: 'permis',
      description: "Certificat d'immatriculation (SAAQ)"
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez enregistrer votre véhicule afin de bénéficier des services de stationnement de la Ville.'
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Certificat d'immatriculation de la SAAQ au nom du demandeur\nou\n• Contrat de location indiquant clairement le numéro de plaque et le titulaire."
    }],
    guide: "Documents acceptés :\n• Certificat d'immatriculation valide au nom du demandeur\n• Le numéro de plaque doit correspondre au véhicule déclaré.",
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Véhicule - Immatriculation invalide',
      descriptionInterne: 'Véhicule - Immatriculation invalide',
      motifCitoyen: "Le certificat d'immatriculation fourni n'est pas valide ou ne correspond pas au demandeur. Veuillez soumettre un certificat d'immatriculation de la SAAQ à votre nom. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-2077 ou par courriel à services@villedewindsor.qc.ca."
    }]
  },
  animaux: {
    justificatifs: [{
      id: '1',
      type: 'carte_id',
      description: 'Carnet de vaccination à jour'
    }],
    instructions: [{
      label: 'Introduction',
      content: 'Veuillez enregistrer votre animal afin d’obtenir sa médaille municipale, tel qu’exigé par le règlement de la Ville.'
    }, {
      label: 'Pièces justificatives',
      content: "Documents acceptés :\n• Carnet de vaccination à jour (rage)\n• Preuve de stérilisation (si applicable)\n• Photo de l'animal."
    }],
    guide: 'Documents acceptés :\n• Carnet de vaccination valide indiquant la vaccination contre la rage\n• Vérifier que la description de l’animal correspond aux documents fournis.',
    refusalLang: 'fr',
    refusals: [{
      id: '1',
      titre: 'Animal - Vaccination non valide',
      descriptionInterne: 'Animal - Vaccination non valide',
      motifCitoyen: "La preuve de vaccination fournie n'est pas valide ou est expirée. Veuillez soumettre un carnet de vaccination à jour incluant la vaccination contre la rage. Nous vous invitons à communiquer avec Services Windsor pour de plus amples informations par téléphone au 819 845-2077 ou par courriel à services@villedewindsor.qc.ca."
    }]
  }
};
export function BcitiIdConfigurer() {
  const [activeSection, setActiveSection] = useState<Section>('cartes');
  const [sections, setSections] = useState<Record<Section, SectionState>>(INITIAL_STATE);
  // Cartes-only card printing state
  const [cartesPrintEnabled, setCartesPrintEnabled] = useState(true);
  const [cardElements, setCardElements] = useState<CardElement[]>([{
    id: 'nom',
    label: 'Nom/Prénom',
    enabled: true
  }, {
    id: 'dob',
    label: 'Date de naissance',
    enabled: true
  }, {
    id: 'photo',
    label: 'Photo',
    enabled: true
  }, {
    id: 'adresse',
    label: 'Adresse',
    enabled: true
  }, {
    id: 'numero',
    label: 'Numéro',
    enabled: true
  }, {
    id: 'barcode',
    label: 'Barcode ou QR code',
    enabled: true
  }, {
    id: 'statut',
    label: 'Statut résident ou non-résident',
    enabled: true
  }, {
    id: 'expiration',
    label: "Date d'expiration",
    enabled: true
  }, {
    id: 'logo',
    label: 'Logo ville',
    enabled: true
  }, {
    id: 'cutting',
    label: 'Aide au découpage (pointillés)',
    enabled: true
  }]);
  const updateSection = (section: Section, patch: Partial<SectionState>) => {
    setSections(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        ...patch
      }
    }));
  };
  const addJustificatif = (section: Section) => {
    const newJustif: Justificatif = {
      id: Date.now().toString(),
      type: 'permis',
      description: ''
    };
    updateSection(section, {
      justificatifs: [...sections[section].justificatifs, newJustif]
    });
  };
  const removeJustificatif = (section: Section, id: string) => {
    updateSection(section, {
      justificatifs: sections[section].justificatifs.filter(j => j.id !== id)
    });
  };
  const updateJustificatif = (section: Section, id: string, field: 'type' | 'description', value: string) => {
    updateSection(section, {
      justificatifs: sections[section].justificatifs.map(j => j.id === id ? {
        ...j,
        [field]: value
      } : j)
    });
  };
  const addRefusal = (section: Section) => {
    const newRefusal: RefusalReason = {
      id: Date.now().toString(),
      titre: '',
      descriptionInterne: '',
      motifCitoyen: ''
    };
    updateSection(section, {
      refusals: [...sections[section].refusals, newRefusal]
    });
  };
  const removeRefusal = (section: Section, id: string) => {
    updateSection(section, {
      refusals: sections[section].refusals.filter(r => r.id !== id)
    });
  };
  const toggleCardElement = (id: string) => {
    setCardElements(cardElements.map(el => el.id === id ? {
      ...el,
      enabled: !el.enabled
    } : el));
  };
  const renderToolbar = () => <div className="flex items-center gap-1 p-2 bg-slate-50 border border-slate-200 rounded-t-lg flex-wrap" data-id="element-775" data-mp="rknt8gz0inta">
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white" data-id="element-776" data-mp="dtiq34wfrde7">
        <option data-id="element-777" data-mp="htt51727he57">Sans Serif</option>
      </select>
      <select className="text-xs border border-slate-300 rounded px-2 py-1 bg-white ml-2" data-id="element-778" data-mp="3vutsaj6lxtx">
        <option data-id="element-779" data-mp="8uo988i13ywq">Normal</option>
      </select>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-780" data-mp="8vh6miv3lc27">
        <button className="p-1 hover:bg-slate-200 rounded" title="H1" data-id="element-781" data-mp="3r12me58h44u2">
          <span className="text-xs font-bold" data-id="element-782" data-mp="3v1mgt11nwuci">H₁</span>
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="H2" data-id="element-783" data-mp="3v3ywt8nv0umx">
          <span className="text-xs font-bold" data-id="element-784" data-mp="1pa8y8tkf6fag">H₂</span>
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-785" data-mp="1tastfrlfn8ty">
        <button className="p-1 hover:bg-slate-200 rounded" title="Bold" data-id="element-786" data-mp="3ds24nol7ksi5">
          <BoldIcon className="w-4 h-4" data-id="element-787" data-mp="3dq7k4bdun2el" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Italic" data-id="element-788" data-mp="39s9u56c98ca5">
          <ItalicIcon className="w-4 h-4" data-id="element-789" data-mp="3nimxeve7b2t5" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Underline" data-id="element-790" data-mp="3nk7vuw3mz5wa">
          <UnderlineIcon className="w-4 h-4" data-id="element-791" data-mp="3iqbztz6xnt3r" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Strikethrough" data-id="element-792" data-mp="1a3uwc4ccmez">
          <StrikethroughIcon className="w-4 h-4" data-id="element-793" data-mp="3sgofpe3syy3k" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-794" data-mp="ac3cb5j9psig">
        <button className="p-1 hover:bg-slate-200 rounded" title="Link" data-id="element-795" data-mp="5d09lrqrfydn">
          <LinkIcon className="w-4 h-4" data-id="element-796" data-mp="5axj76lp438e" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Code" data-id="element-797" data-mp="2c3zmaapseub4">
          <CodeIcon className="w-4 h-4" data-id="element-798" data-mp="274mzfpyx1n8j" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Quote" data-id="element-799" data-mp="2767ynsw6ducq">
          <QuoteIcon className="w-4 h-4" data-id="element-800" data-mp="29i8o6eirtmx8" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-801" data-mp="2djlx4xh5u68d">
        <button className="p-1 hover:bg-slate-200 rounded" title="Text color" data-id="element-802" data-mp="2ijq4ny03bqmg">
          <span className="text-xs font-bold" data-id="element-803" data-mp="2nijgzn3n5bl1">A</span>
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-804" data-mp="2nggsj7i4zqif">
        <button className="p-1 hover:bg-slate-200 rounded" title="Bullet list" data-id="element-805" data-mp="1pwdny4gm2d12">
          <ListIcon className="w-4 h-4" data-id="element-806" data-mp="1uunn0q5ghxry" />
        </button>
        <button className="p-1 hover:bg-slate-200 rounded" title="Numbered list" data-id="element-807" data-mp="1zsyu4t4d9u6h">
          <ListOrderedIcon className="w-4 h-4" data-id="element-808" data-mp="24k77g08thzox" />
        </button>
      </div>
      <div className="flex items-center gap-1 ml-2 border-l border-slate-300 pl-2" data-id="element-809" data-mp="24li7boz56nar">
        <button className="p-1 hover:bg-slate-200 rounded" title="Align" data-id="element-810" data-mp="17273eb62lxqj">
          <AlignLeftIcon className="w-4 h-4" data-id="element-811" data-mp="1c1themtq3i0e" />
        </button>
      </div>
    </div>;
  const renderJustificatifs = (section: Section) => {
    const justifs = sections[section].justificatifs;
    return <div className="space-y-4" data-id="element-812" data-mp="zlk1khkggejp">
        <div className="flex items-center justify-between" data-id="element-813" data-mp="ulp9wbsuvkxl">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-814" data-mp="uk4iuw5r2k4h">
            Pièces justificatives
          </label>
          <button onClick={() => addJustificatif(section)} className="text-sm text-[#4F46E5] hover:text-[#4338CA] font-medium flex items-center gap-1" data-id="element-815" data-mp="lkr7fxy2flh4">
            <PlusIcon className="w-4 h-4" data-id="element-816" data-mp="bv7mmmqslhjm" />
            Ajouter
          </button>
        </div>
        {justifs.map((justif, idx) => <div key={justif.id} className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3" data-id="element-817" data-mp="6x617v5w964z" data-mpi={idx}>
            <div className="flex items-center justify-between" data-id="element-818" data-mp="1z4hd80n92sg">
              <span className="text-sm font-medium text-slate-700" data-id="element-819" data-mp="3u242nyyemzxu">
                Document {idx + 1}
              </span>
              <button onClick={() => removeJustificatif(section, justif.id)} className="text-slate-400 hover:text-red-600" data-id="element-820" data-mp="3jee99l1mepkh">
                <TrashIcon className="w-4 h-4" data-id="element-821" data-mp="3agca7b1g66zu" />
              </button>
            </div>
            <div data-id="element-822" data-mp="3ff5d69j3yflr">
              <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-823" data-mp="3fenkfmvnmjil">
                Type
              </label>
              <select value={justif.type} onChange={e => updateJustificatif(section, justif.id, 'type', e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-824" data-mp="70ewwt7zoixa">
                <option value="permis" data-id="element-825" data-mp="pwek7qq0uyaj">Permis de conduire</option>
                <option value="carte_id" data-id="element-826" data-mp="uoeotwh1efdg">Carte d'identité</option>
                <option value="passeport" data-id="element-827" data-mp="upprtu2nrj3i">Passeport</option>
              </select>
            </div>
            <div data-id="element-828" data-mp="140c1n2ft4se8">
              <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-829" data-mp="101d07rtabdrj">
                Description
              </label>
              <textarea value={justif.description} onChange={e => updateJustificatif(section, justif.id, 'description', e.target.value)} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" placeholder="Description du document requis" data-id="element-830" data-mp="1drgftzxns980" />
            </div>
          </div>)}
      </div>;
  };
  const renderInstructions = (section: Section) => {
    const instructions = sections[section].instructions;
    return <div className="space-y-6" data-id="element-831" data-mp="3vyawgamr7mdw">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-832" data-mp="3vzc6qcvy44uk">
          Messages d'instructions
        </label>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-833" data-mp="3sfgq5cctiot">
          <div className="space-y-6" data-id="element-834" data-mp="8stcop0ol26h">
            {instructions.map((field, idx) => <div key={idx} data-id="element-835" data-mp="dld0sfwzvfpj" data-mpi={idx}>
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-836" data-mp="dlmi7f3z4ejm">
                  {field.label}
                </label>
                {renderToolbar()}
                <textarea value={field.content} onChange={e => {
              const updated = instructions.map((f, i) => i === idx ? {
                ...f,
                content: e.target.value
              } : f);
              updateSection(section, {
                instructions: updated
              });
            }} rows={3} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-837" data-mp="nhg43p3l1ke2" />
              </div>)}
          </div>
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-838" data-mp="2c36ic9li12qc">
            <div className="flex items-center gap-2 mb-4" data-id="element-839" data-mp="39qf3u4li2bsc">
              <SmartphoneIcon className="w-5 h-5 text-slate-600" data-id="element-840" data-mp="39nj90p4y8384" />
              <span className="text-sm font-semibold text-slate-700" data-id="element-841" data-mp="34s3uduz4a3an">
                Aperçu citoyen
              </span>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-4 max-h-[600px] overflow-y-auto" data-id="element-842" data-mp="2ztk6vq4vkjg6">
              {instructions.map((field, idx) => <div key={idx} className="border-b border-slate-100 pb-3 last:border-0" data-id="element-843" data-mp="1odc2rk9sp02e" data-mpi={idx}>
                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1" data-id="element-844" data-mp="kmh31e39adft">
                    {field.label}
                  </h4>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap" data-id="element-845" data-mp="fuplft22vi1t">
                    {field.content}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderGuide = (section: Section) => {
    const guide = sections[section].guide;
    return <div className="space-y-3" data-id="element-846" data-mp="13j8sy6dazqsu">
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-847" data-mp="ylqjmevdj9kb">
          Guide d'approbation
        </label>
        {renderToolbar()}
        <textarea value={guide} onChange={e => updateSection(section, {
        guide: e.target.value
      })} rows={6} className="w-full px-3 py-2 border border-slate-200 border-t-0 rounded-b-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-848" data-mp="umreat5qdasc" />
      </div>;
  };
  const renderRefusals = (section: Section) => {
    const refusals = sections[section].refusals;
    const lang = sections[section].refusalLang;
    const sectionLabel = SECTION_LABELS[section];
    return <div className="space-y-4" data-id="element-849" data-mp="1ysc7duzw09rl">
        <div className="flex items-center justify-between" data-id="element-850" data-mp="23losvw99s0g4">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-851" data-mp="23n9n1kro9hdg">
            Motifs de refus — {sectionLabel}
          </h3>
          <button onClick={() => addRefusal(section)} className="px-4 py-2 text-sm font-medium text-[#4F46E5] border border-[#4F46E5] rounded-lg hover:bg-[#4F46E5] hover:text-white transition-colors flex items-center gap-2" data-id="element-852" data-mp="2djcqcty9fm1z">
            <PlusIcon className="w-4 h-4" data-id="element-853" data-mp="2mj83v5faft56" />
            Ajouter un motif
          </button>
        </div>

        <div className="flex items-center gap-4 border-b border-slate-200" data-id="element-854" data-mp="o67ribjxy121">
          <button onClick={() => updateSection(section, {
          refusalLang: 'fr'
        })} className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${lang === 'fr' ? 'text-[#4F46E5] border-[#4F46E5]' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-id="element-855" data-mp="s6rq7jk5xcqa">
            Français
          </button>
          <button onClick={() => updateSection(section, {
          refusalLang: 'en'
        })} className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 ${lang === 'en' ? 'text-[#4F46E5] border-[#4F46E5]' : 'text-slate-500 border-transparent hover:text-slate-700'}`} data-id="element-856" data-mp="20f7f3mexui0d">
            Anglais
          </button>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-857" data-mp="2j79xb278a6cq">
          <table className="w-full" data-id="element-858" data-mp="2x713whvf0kgp">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-859" data-mp="2x3wxreye3ene">
              <tr data-id="element-860" data-mp="2s8zoc6x7739z">
                <th className="w-8" data-id="element-861" data-mp="2s5bsv28706sm"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-862" data-mp="3bsor6bwx053s">
                  Titre
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-863" data-mp="3gpozyul5flyl">
                  Description interne
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-864" data-mp="36saiqsreit58">
                  Motif affiché au citoyen
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide w-24" data-id="element-865" data-mp="2xu8kgkz1yeln">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-866" data-mp="2o45j9xhs1uc8">
              {refusals.map(refusal => <tr key={refusal.id} className="hover:bg-slate-50" data-id="element-867" data-mp="2a4xgkumeo43a">
                  <td className="px-2 py-3" data-id="element-868" data-mp="2f37cj7fyf8py">
                    <GripVerticalIcon className="w-4 h-4 text-slate-400 cursor-move" data-id="element-869" data-mp="2f1w8r7mie0yu" />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700" data-id="element-870" data-mp="27442cpuchdpl">
                    {refusal.titre}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700" data-id="element-871" data-mp="1xbycpmrkf8dk">
                    {refusal.descriptionInterne}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-md truncate" data-id="element-872" data-mp="1t9tl0tkogltk">
                    {refusal.motifCitoyen}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-873" data-mp="1jcf6izibe73w">
                    <div className="flex items-center justify-center gap-2" data-id="element-874" data-mp="1jaucrc3tqs73">
                      <button className="text-slate-400 hover:text-[#4F46E5]" data-id="element-875" data-mp="1eedlwa58pzqb">
                        <PencilIcon className="w-4 h-4" data-id="element-876" data-mp="1ac8uzj66fh7d" />
                      </button>
                      <button onClick={() => removeRefusal(section, refusal.id)} className="text-slate-400 hover:text-red-600" data-id="element-877" data-mp="38r3g6szitay8">
                        <TrashIcon className="w-4 h-4" data-id="element-878" data-mp="qi8vvj12o486" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>;
  };
  const renderCardPrinting = () => {
    return <div className="space-y-6" data-id="element-879" data-mp="149dzsxbh3iob">
        <div className="flex items-center justify-between" data-id="element-880" data-mp="148cd1vl3h7qr">
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-881" data-mp="7j4sapsav2nw">
            Impression de la carte
          </label>
          <button onClick={() => setCartesPrintEnabled(!cartesPrintEnabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cartesPrintEnabled ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-882" data-mp="cj0xgscqh051">
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cartesPrintEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-883" data-mp="1rz8c9drvr581" />
          </button>
        </div>

        {cartesPrintEnabled && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-884" data-mp="3szedi9evgdr0">
            <div className="space-y-4" data-id="element-885" data-mp="3oykotdtluux2">
              <h4 className="text-sm font-semibold text-slate-700" data-id="element-886" data-mp="3p1oygqpuaiv4">
                Éléments sur la carte
              </h4>
              <div className="space-y-2" data-id="element-887" data-mp="1v65752efk83">
                {cardElements.map(element => <div key={element.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-888" data-mp="3aapvpwboqojg">
                    <span className="text-sm text-slate-700" data-id="element-889" data-mp="359k09qqn4n68">
                      {element.label}
                    </span>
                    <button onClick={() => toggleCardElement(element.id)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${element.enabled ? 'bg-[#4F46E5]' : 'bg-slate-300'}`} data-id="element-890" data-mp="3f9sy190a6gnq">
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${element.enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-891" data-mp="2mjb0enibt7hp" />
                    </button>
                  </div>)}
              </div>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl border border-slate-200" data-id="element-892" data-mp="x3qhomfq9peg">
              <div className="flex items-center gap-2 mb-4" data-id="element-893" data-mp="x1nndq7jla3x">
                <CreditCardIcon className="w-5 h-5 text-slate-600" data-id="element-894" data-mp="1ku433u3t4q02" />
                <span className="text-sm font-semibold text-slate-700" data-id="element-895" data-mp="1psxglmj3gh08">
                  Aperçu de la carte
                </span>
              </div>
              <div className={`bg-gradient-to-br from-[#4F46E5] to-[#6366F1] rounded-xl p-6 shadow-lg relative ${cardElements.find(el => el.id === 'cutting')?.enabled ? 'border-2 border-dashed border-slate-400' : ''}`} style={{
            aspectRatio: '1.586/1'
          }} data-id="element-896" data-mp="1bv0gwh9cfuhc">
                {cardElements.find(el => el.id === 'logo')?.enabled && <div className="absolute top-4 left-4" data-id="element-897" data-mp="s1bvnb96a79s">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-898" data-mp="rzr29oylgudi">
                      <span className="text-white font-bold text-xs" data-id="element-899" data-mp="n2hfyi6i4ytw">LOGO</span>
                    </div>
                  </div>}
                <div className="flex flex-col h-full justify-between text-white" data-id="element-900" data-mp="1fqn0p4xk2ntq">
                  <div className="flex items-start gap-4" data-id="element-901" data-mp="1artmfaag2ssi">
                    {cardElements.find(el => el.id === 'photo')?.enabled && <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center mt-16" data-id="element-902" data-mp="1601twu7qaczq">
                        <span className="text-xs" data-id="element-903" data-mp="1117dfyx8h0is">Photo</span>
                      </div>}
                    <div className="flex-1 mt-16 space-y-1" data-id="element-904" data-mp="1yiz4lw3uxx5z">
                      {cardElements.find(el => el.id === 'nom')?.enabled && <div className="text-sm font-bold" data-id="element-905" data-mp="2122drks1311c">Jean Dupont</div>}
                      {cardElements.find(el => el.id === 'dob')?.enabled && <div className="text-xs opacity-90" data-id="element-906" data-mp="262qfb0dvv09t">15 mars 1985</div>}
                      {cardElements.find(el => el.id === 'adresse')?.enabled && <div className="text-xs opacity-90" data-id="element-907" data-mp="2k0ojxe2ejic4">
                          11 rue St-Georges, Windsor
                        </div>}
                    </div>
                  </div>
                  <div className="flex items-end justify-between" data-id="element-908" data-mp="2tpyagphlksy4">
                    <div className="space-y-1" data-id="element-909" data-mp="2tsu7mblmfdli">
                      {cardElements.find(el => el.id === 'numero')?.enabled && <div className="text-xs font-mono" data-id="element-910" data-mp="33pyuyer7df3w">No: 123456789</div>}
                      {cardElements.find(el => el.id === 'statut')?.enabled && <div className="text-xs" data-id="element-911" data-mp="e9r1twluh4pe">Résident</div>}
                      {cardElements.find(el => el.id === 'expiration')?.enabled && <div className="text-xs" data-id="element-912" data-mp="4c2y4h69gmv3">Exp: 31/12/2025</div>}
                    </div>
                    {cardElements.find(el => el.id === 'barcode')?.enabled && <div className="w-16 h-16 bg-white/20 rounded flex items-center justify-center" data-id="element-913" data-mp="x1jlh0f0xdkg">
                        <span className="text-xs" data-id="element-914" data-mp="11yjt3friuodo">QR</span>
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  };
  const renderSectionContent = () => {
    return <div className="space-y-8" data-id="element-915" data-mp="1fz4biatiyi6n">
        {renderJustificatifs(activeSection)}
        {renderInstructions(activeSection)}
        {renderGuide(activeSection)}
        {renderRefusals(activeSection)}
        {activeSection === 'cartes' && renderCardPrinting()}
      </div>;
  };
  return <div className="flex gap-6" data-id="element-916" data-mp="30hx1tv3806v1">
      {/* Section Switcher */}
      <div className="w-48 flex-shrink-0" data-id="element-917" data-mp="30fkk9j1djyii">
        <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-918" data-mp="3f6ksbmy7lqe4">
          {SECTION_ORDER.map(section => <button key={section} onClick={() => setActiveSection(section)} className={`w-full px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeSection === section ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-919" data-mp="3a9b6eha13wv1">
              {SECTION_LABELS[section]}
            </button>)}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-920" data-mp="ar1u9k8uwxst">
        {renderSectionContent()}
      </div>
    </div>;
}